import { normalizeRole, normalizeOrgRole, VALID_ROLES } from '../utils/roles.js'

export const ROLE_HIERARCHY = {
  user: 1,
  manager: 2,
  admin: 3,
}

export const ROLE_PERMISSIONS = {
  admin: [
    '*',
    'manageSubOrgs',
  ],

  manager: [
    'createLottery',
    'assignUsers',
    'assignManagers',
    'uploadBatch',
    'drawWinner',
    'viewLotteries',
    'viewBatches',
    'viewDrawHistory',
    'manageUsers',
    'view',
    'edit',
  ],

  user: [
    'uploadBatch',
    'viewLotteries',
    'viewBatches',
    'viewDrawHistory',
    'view',
  ],
}

export const KNOWN_ACTIONS = new Set([
  'createLottery',
  'assignUsers',
  'assignManagers',
  'uploadBatch',
  'drawWinner',
  'viewLotteries',
  'viewBatches',
  'viewDrawHistory',
  'manageUsers',
  'manageSubOrgs',
  'view',
  'edit',
])
export const ALLOWED_SCOPES = new Set(['org', 'public'])

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const toNullableString = (value) => {
  if (value === null || value === undefined || value === '') return null
  return String(value)
}

export const toOrganization = (value = {}) => ({
  id: toNumberOrNull(value.id),
  name: String(value.name ?? ''),
  role: normalizeOrgRole(value.role),
  // Per auth-contract.md: is_deleted = null, False, or True
  // null/False = active, True = soft-deleted
  is_deleted: value.is_deleted === true,
})

export const toSubOrganization = (value = {}) => ({
  id: toNumberOrNull(value.id),
  organization_id: toNumberOrNull(value.organization_id),
  name: String(value.name ?? ''),
  // Per auth-contract.md: is_deleted = null, False, or True
  is_deleted: value.is_deleted === true,
})

export const toUser = (value = {}) => {
  if (!value || typeof value !== 'object') {
    return {
      id: null,
      full_name: '',
      username: '',
      email: '',
      phone: '',
      role: null,
      organization_id: null,
      must_change_password: false,
      is_superadmin: false,
      sub_org_ids: [],
    }
  }

  const memberships = Array.isArray(value.sub_org_memberships) ? value.sub_org_memberships : []
  const sub_org_ids = memberships
    .map((m) => toNumberOrNull(m?.sub_org_id))
    .filter((id) => id !== null && Number.isInteger(id) && id > 0)

  const legacySubOrgId = toNumberOrNull(value.sub_org_id)
  if (
    import.meta.env.DEV
    && Number.isInteger(legacySubOrgId)
    && legacySubOrgId > 0
    && !sub_org_ids.includes(legacySubOrgId)
  ) {
    console.warn('Legacy sub_org_id mismatch with memberships', {
      userId: toNumberOrNull(value.id),
      legacySubOrgId,
      sub_org_ids,
    })
  }

  return {
    id: toNumberOrNull(value.id),
    full_name: String(value.full_name ?? ''),
    username: String(value.username ?? ''),
    email: String(value.email ?? ''),
    phone: String(value.phone ?? ''),
    role: normalizeRole(value.role),
    organization_id: toNumberOrNull(value.organization_id),
    must_change_password: value.must_change_password === true,
    is_superadmin: value.is_superadmin === true,
    sub_org_ids,
  }
}

export const toUserContext = (user = null) => {
  if (!user || typeof user !== 'object') {
    return {
      id: null,
      role: null,
      isSuperAdmin: false,
      organization_id: null,
    }
  }

  return {
    id: toNumberOrNull(user.id),
    role: normalizeRole(user.role),
    isSuperAdmin: user.is_superadmin === true,
    organization_id: toNumberOrNull(user.organization_id),
  }
}

export const toRbacContext = (authState = {}, orgState = {}) => {
  const userContext = toUserContext(authState.user)
  const isSuperAdmin = authState.isSuperAdmin === true || userContext.isSuperAdmin === true

  return {
    user: userContext,
    isSuperAdmin,
    activeOrgRole: normalizeOrgRole(orgState.activeOrgRole),
    activeOrgId: toNumberOrNull(orgState.activeOrgId),
  }
}

export const toLotteryContext = (lotteryState = {}) => {
  const rawLotteries = Array.isArray(lotteryState.lotteries) ? lotteryState.lotteries : []
  const lotteries = rawLotteries.map((item) => ({
    ...item,
    id: toNumberOrNull(item?.id ?? item?.lottery_id),
    sub_org_id: toNumberOrNull(item?.sub_org_id),
    deadline_at: toNullableString(item?.deadline_at),
  }))

  return {
    lotteries,
    accessibleLotteryIds: new Set((Array.isArray(lotteryState.accessibleLotteryIds) ? lotteryState.accessibleLotteryIds : []).map(String)),
    activeSubOrgId: toNumberOrNull(lotteryState.activeSubOrgId),
  }
}

const resolveRoleForScope = (rbacContext, scope = 'org') => {
  if (scope === 'org') {
    return rbacContext.activeOrgRole
  }

  return rbacContext.activeOrgRole || rbacContext.user.role
}

export const evaluatePermission = (rbacContext, action, context = {}) => {
  if (rbacContext.isSuperAdmin) return true
  if (!KNOWN_ACTIONS.has(action)) return false
  if (!context || typeof context !== 'object') return false
  if (!ALLOWED_SCOPES.has(context.scope)) return false

  const role = resolveRoleForScope(rbacContext, context.scope)
  if (!role || !VALID_ROLES.includes(role)) return false

  const allowed = ROLE_PERMISSIONS[role] ?? []
  return allowed.includes('*') || allowed.includes(action)
}

const getLotteryId = (lottery) => lottery?.id ?? lottery?.lottery_id

const getVisibleLotteries = (rbacContext, lotteryContext) => {
  const hasManageUsers = evaluatePermission(rbacContext, 'manageUsers', { scope: 'org' })

  if (hasManageUsers) {
    return lotteryContext.lotteries
  }

  return lotteryContext.lotteries.filter((lottery) => lotteryContext.accessibleLotteryIds.has(String(getLotteryId(lottery))))
}

export const evaluateUserContext = (user, orgState = {}, lotteryState = {}) => {
  const rbacContext = toRbacContext({ user, isSuperAdmin: user?.is_superadmin === true }, orgState)
  const lotteryContext = toLotteryContext({
    lotteries: lotteryState.lotteries,
    accessibleLotteryIds: lotteryState.accessibleLotteryIds,
    activeSubOrgId: orgState.activeSubOrgId,
  })

  const permissions = {
    createLottery: evaluatePermission(rbacContext, 'createLottery', { scope: 'org' }),
    viewLotteries: evaluatePermission(rbacContext, 'viewLotteries', { scope: 'org' }),
    assignUsers: evaluatePermission(rbacContext, 'assignUsers', { scope: 'org' }),
    assignManagers: evaluatePermission(rbacContext, 'assignManagers', { scope: 'org' }),
    uploadBatch: evaluatePermission(rbacContext, 'uploadBatch', { scope: 'org' }),
    drawWinner: evaluatePermission(rbacContext, 'drawWinner', { scope: 'org' }),
    viewBatches: evaluatePermission(rbacContext, 'viewBatches', { scope: 'org' }),
    viewDrawHistory: evaluatePermission(rbacContext, 'viewDrawHistory', { scope: 'org' }),
    manageUsers: evaluatePermission(rbacContext, 'manageUsers', { scope: 'org' }),
  }

  const role = rbacContext.activeOrgRole || rbacContext.user.role
  const visibleOrgs = Array.isArray(orgState.organizations)
    ? orgState.organizations.filter((org) => rbacContext.isSuperAdmin || Number(org?.id) === Number(rbacContext.activeOrgId))
    : []

  return {
    permissions,
    visibleOrgs,
    visibleLotteries: getVisibleLotteries(rbacContext, lotteryContext),
    flags: {
      isSuperAdmin: rbacContext.isSuperAdmin,
      isAdmin: rbacContext.isSuperAdmin || role === 'admin',
      isManager: rbacContext.isSuperAdmin || role === 'admin' || role === 'manager',
      isUser: rbacContext.isSuperAdmin || role === 'admin' || role === 'manager' || role === 'user',
    },
  }
}
